import { Client, xml } from "@xmpp/client"
import iqMeasure from "../instrumentation/iqMeasure"
import { extractVhostFromJid } from "../jidUtils"
import { PlannedOrder } from "@/reducers/plannedOrdersSlice"

const WIZARD = "wizard"
const COMMANDS_NS = "http://jabber.org/protocol/commands"
const JSON_NS = "urn:xmpp:json:0"

// Dev escape hatch: point planned-order commands at a non-default gps
// resource (e.g. "gps-local" or a staging test resource) — see gps/CLAUDE.md.
const GPS_RESOURCE_STORAGE_KEY = "slickshift:gpsResource"

const plannedOrdersTarget = (client: Client): string => {
  const vhost = extractVhostFromJid(client.jid!.toString())
  let resource = "gps"
  try {
    resource = localStorage.getItem(GPS_RESOURCE_STORAGE_KEY) || "gps"
  } catch {
    // storage unavailable — keep default
  }
  return `${WIZARD}@${vhost}/${resource}`
}

// Backend keeps assignment state in snake_case columns; stops/metadata travel
// as opaque JSON payload.
const serializeOrder = (order: PlannedOrder) => ({
  id: order.id,
  assignment_status: order.assignmentStatus,
  assigned_dispatcher_jid: order.assignedDispatcherJid,
  assigned_vehicle_chat_jid: order.assignedVehicleChatJid,
  created_by: order.createdBy,
  stops: order.stops,
  metadata: order.metadata,
  created_at_client: order.createdAt,
  navigator_transport_id: order.navigatorTransportId,
})

type BackendPlannedOrder = {
  id: string
  assignment_status?: string
  assigned_dispatcher_jid?: string | null
  assigned_vehicle_chat_jid?: string | null
  created_by?: string | null
  created_at?: string
  updated_at?: string
  created_at_client?: string
  stops?: PlannedOrder["stops"]
  metadata?: PlannedOrder["metadata"]
  navigator_transport_id?: string | null
}

const deserializeOrder = (raw: BackendPlannedOrder): PlannedOrder | null => {
  if (!raw.id) return null
  const status = raw.assignment_status
  return {
    id: raw.id,
    stops: raw.stops ?? [],
    metadata: raw.metadata,
    assignmentStatus:
      status === "dispatcher" || status === "vehicle" ? status : "unassigned",
    assignedDispatcherJid: raw.assigned_dispatcher_jid ?? undefined,
    assignedVehicleChatJid: raw.assigned_vehicle_chat_jid ?? undefined,
    createdBy: raw.created_by ?? undefined,
    createdAt: raw.created_at_client ?? raw.created_at ?? "",
    updatedAt: raw.updated_at,
    navigatorTransportId: raw.navigator_transport_id ?? undefined,
  }
}

const parseJsonReply = (reply: {
  getChild: (
    name: string,
  ) =>
    | { getChildText: (name: string, ns?: string) => string | null }
    | undefined
}): unknown => {
  const text = reply.getChild("command")?.getChildText("json", JSON_NS)
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

const PlannedOrdersApi = {
  upsert: async (client: Client, order: PlannedOrder): Promise<void> => {
    const measuredIqCaller = iqMeasure(client.iqCaller)
    await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: plannedOrdersTarget(client) },
        xml(
          "command",
          {
            node: "upsert_planned_delivery_order",
            action: "execute",
            xmlns: COMMANDS_NS,
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "order", type: "text-single" },
              xml("value", {}, JSON.stringify(serializeOrder(order))),
            ),
          ),
        ),
      ),
      30000,
    )
  },

  // gps pages results (see planned_delivery_order_management.py) so a single
  // IQ stanza never exceeds ejabberd's max_stanza_size — Oskar's Navigator
  // pull alone returns 190+ orders, large enough to get the connection
  // dropped mid-write when sent as one stanza. Follow next_after_id until
  // the backend reports has_more: false.
  fetchAll: async (client: Client): Promise<PlannedOrder[]> => {
    const measuredIqCaller = iqMeasure(client.iqCaller)
    const orders: PlannedOrder[] = []
    let afterId: string | null = null

    while (true) {
      const fields = afterId
        ? [
            xml(
              "field",
              { var: "after_id", type: "text-single" },
              xml("value", {}, afterId),
            ),
          ]
        : []
      const reply = await measuredIqCaller.request(
        xml(
          "iq",
          { from: client.jid, type: "set", to: plannedOrdersTarget(client) },
          xml(
            "command",
            {
              node: "get_planned_delivery_orders",
              action: "execute",
              xmlns: COMMANDS_NS,
            },
            ...(fields.length > 0
              ? [xml("x", { xmlns: "jabber:x:data", type: "form" }, ...fields)]
              : []),
          ),
        ),
        30000,
      )
      const payload = parseJsonReply(reply) as {
        orders?: BackendPlannedOrder[]
        has_more?: boolean
        next_after_id?: string | null
      } | null

      orders.push(
        ...(payload?.orders ?? [])
          .map(deserializeOrder)
          .filter((order): order is PlannedOrder => order !== null),
      )

      if (!payload?.has_more || !payload.next_after_id) break
      afterId = payload.next_after_id
    }

    return orders
  },

  remove: async (client: Client, orderId: string): Promise<void> => {
    const measuredIqCaller = iqMeasure(client.iqCaller)
    await measuredIqCaller.request(
      xml(
        "iq",
        { from: client.jid, type: "set", to: plannedOrdersTarget(client) },
        xml(
          "command",
          {
            node: "delete_planned_delivery_order",
            action: "execute",
            xmlns: COMMANDS_NS,
          },
          xml(
            "x",
            { xmlns: "jabber:x:data", type: "form" },
            xml(
              "field",
              { var: "order_id", type: "text-single" },
              xml("value", {}, orderId),
            ),
          ),
        ),
      ),
      30000,
    )
  },
}

export default PlannedOrdersApi
